import { useState } from "react";
import { Plus, Search, Users, Music2, Pencil, Trash2 } from "lucide-react";
import { Header } from "@/components/Layout/Header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { TeamForm } from "@/components/Teams/TeamForm";
import { TeamMembersSection } from "@/components/Teams/TeamMembersSection";
import {
  useTeams,
  useDeleteTeam,
  type TeamWithMembers,
} from "@/hooks/useTeams";
import { useToast } from "@/hooks/useToast";

export function TeamsPage() {
  const { data: teams = [], isLoading } = useTeams();
  const deleteTeam = useDeleteTeam();
  const { toast } = useToast();

  const [search, setSearch] = useState("");
  const [formOpen, setFormOpen] = useState(false);
  const [editingTeam, setEditingTeam] = useState<TeamWithMembers | null>(null);
  const [deletingTeam, setDeletingTeam] = useState<TeamWithMembers | null>(
    null,
  );

  const filteredTeams = teams.filter(
    (t) =>
      t.name.toLowerCase().includes(search.toLowerCase()) ||
      t.category.toLowerCase().includes(search.toLowerCase()),
  );

  const handleEdit = (team: TeamWithMembers) => {
    setEditingTeam(team);
    setFormOpen(true);
  };

  const handleCloseForm = () => {
    setFormOpen(false);
    setEditingTeam(null);
  };

  const handleDelete = async () => {
    if (!deletingTeam) return;
    try {
      await deleteTeam.mutateAsync(deletingTeam.id);
      toast({ title: "Équipe supprimée" });
    } catch {
      toast({
        title: "Erreur",
        description: "Impossible de supprimer l'équipe",
        variant: "destructive",
      });
    } finally {
      setDeletingTeam(null);
    }
  };

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col">
      <Header title="Équipes" />

      <div className="flex-1 space-y-4 p-4 md:p-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="relative w-full sm:w-80">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Rechercher une équipe..."
              className="pl-9"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          <Button
            onClick={() => setFormOpen(true)}
            className="w-full sm:w-auto"
          >
            <Plus className="mr-2 h-4 w-4" />
            Nouvelle équipe
          </Button>
        </div>

        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {filteredTeams.map((team) => (
            <Card key={team.id}>
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <Music2 className="h-5 w-5" />
                      {team.name}
                    </CardTitle>
                    <CardDescription>{team.description}</CardDescription>
                  </div>
                  <Badge variant="secondary">{team.category}</Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-3 text-sm">
                {team.leader && (
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Users className="h-4 w-4" />
                    <span>Leader: {team.leader.full_name}</span>
                  </div>
                )}
                {team.max_capacity && (
                  <p className="text-muted-foreground">
                    Capacité max: {team.max_capacity} personnes
                  </p>
                )}
                <Badge variant={team.is_active ? "default" : "secondary"}>
                  {team.is_active ? "Active" : "Inactive"}
                </Badge>

                <TeamMembersSection teamId={team.id} teamName={team.name} />

                <div className="flex gap-2 pt-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleEdit(team)}
                  >
                    <Pencil className="mr-1 h-3 w-3" />
                    Modifier
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setDeletingTeam(team)}
                  >
                    <Trash2 className="mr-1 h-3 w-3" />
                    Supprimer
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {filteredTeams.length === 0 && (
          <div className="py-12 text-center text-muted-foreground">
            Aucune équipe trouvée
          </div>
        )}
      </div>

      <TeamForm
        open={formOpen}
        onClose={handleCloseForm}
        team={editingTeam ?? undefined}
      />

      <AlertDialog
        open={!!deletingTeam}
        onOpenChange={(open) => !open && setDeletingTeam(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer l'équipe</AlertDialogTitle>
            <AlertDialogDescription>
              Êtes-vous sûr de vouloir supprimer "{deletingTeam?.name}" ? Cette
              action est irréversible.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete}>
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
